import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getSportsStage } from 'api/services/sports';
import { getRegion } from 'utils/Region';

const useSportStage = () => {
    const routeList = useSelector(state => state.nav.nodeList);
    const sportsConfig = useSelector(state => state.metadata.sports_competition_config);
    const { pathname } = useLocation();

    const sports_worldcup_phase2_gracenote_config = useSelector(state => {
        if (state.metadata.sports_worldcup_phase2_gracenote_config) {
          const config = JSON.parse(state.metadata.sports_worldcup_phase2_gracenote_config);
          const region = getRegion();
          if (config[region]) {
            return config[region].enabled;
          }
          return config.default?.enabled ?? false;
        }
        return false;
      });

    const lastSegment = pathname.split('/').filter(Boolean).pop();

    const currentNode = useMemo(() => {
        if (!routeList || !lastSegment) return null;
        return routeList.find(node => node.code === lastSegment) || null;
    }, [routeList, lastSegment]);

    const leagueId = useMemo(() => {
        if (!sportsConfig || !lastSegment) return null;
        try {
            const config = JSON.parse(sportsConfig);
            return config[lastSegment]?.leagueId || null;
        } catch {
            return null;
        }
    }, [sportsConfig, lastSegment]);

    const stages = useMemo(() => {
        if (!currentNode?.childs) return [];
        return currentNode.childs.map(child => {
            try {
                const behaviour = JSON.parse(child.app_behaviour);
                return {
                    code: behaviour.stage_code,
                    label: child.text,
                };
            } catch {
                return null;
            }
        }).filter(Boolean);
    }, [currentNode]);

    const [stageData, setStageData] = useState(null);
    const [nextStageData, setNextStageData] = useState(null);
    const [thirdPlaceData, setThirdPlaceData] = useState(null);
    const [stageLoading, setStageLoading] = useState(false);
    const [stageError, setStageError] = useState(null);

    const fetchIdRef = useRef(0);

    useEffect(() => {
        return () => { fetchIdRef.current = -Infinity; };
    }, []);

    const fetchStage = useCallback(async (stageCode) => {
        if (!leagueId || !stageCode) return;

        const myId = ++fetchIdRef.current;
        const isStale = () => fetchIdRef.current !== myId;

        const currentIndex = stages.findIndex(s => s.code === stageCode);
        const isFirst = currentIndex === 0;
        const isLast = currentIndex === stages.length - 1;
        const codes = stageCode.split(',');
        const isComposite = codes.length > 1;

        setStageLoading(true);
        setStageError(null);
        setNextStageData(null);
        setThirdPlaceData(null);

        try {
            if (isComposite) {
                // Ej: "SEMI_FINALS,THIRD_PLACE" → 3 llamadas: SEMI_FINALS, FINAL (next), THIRD_PLACE
                const [primaryRes] = await Promise.all([
                    getSportsStage(leagueId, codes[0], sports_worldcup_phase2_gracenote_config ? 'v2' : 'v1'),
                ]);
                if (isStale()) return;
                setStageData(primaryRes.data);

                // Next stage (FINAL) - silencioso
                const nextIndex = currentIndex + 1;
                if (nextIndex < stages.length) {
                    try {
                        const nextCodes = stages[nextIndex].code.split(',');
                        const nextRes = await getSportsStage(leagueId, nextCodes[0], sports_worldcup_phase2_gracenote_config ? 'v2' : 'v1');
                        if (!isStale()) setNextStageData(nextRes.data);
                    } catch {
                        if (!isStale()) setNextStageData(null);
                    }
                }

                // Third place - silencioso
                try {
                    const thirdRes = await getSportsStage(leagueId, codes[1].trim(),sports_worldcup_phase2_gracenote_config ? 'v2' : 'v1');
                    if (!isStale()) setThirdPlaceData(thirdRes.data);
                } catch {
                    if (!isStale()) setThirdPlaceData(null);
                }
            } else {
                const currentRes = await getSportsStage(leagueId, stageCode, sports_worldcup_phase2_gracenote_config ? 'v2' : 'v1');
                if (isStale()) return;
                setStageData(currentRes.data);

                if (!isFirst && !isLast) {
                    try {
                        const nextCodes = stages[currentIndex + 1].code.split(',');
                        const nextRes = await getSportsStage(leagueId, nextCodes[0],sports_worldcup_phase2_gracenote_config ? 'v2' : 'v1');
                        if (!isStale()) setNextStageData(nextRes.data);
                    } catch {
                        if (!isStale()) setNextStageData(null);
                    }
                }

                if (isLast && currentIndex > 0) {
                    const prevCodes = stages[currentIndex - 1].code.split(',');
                    if (prevCodes.length > 1) {
                        try {
                            const thirdRes = await getSportsStage(leagueId, prevCodes[1].trim(), sports_worldcup_phase2_gracenote_config ? 'v2' : 'v1');
                            if (!isStale()) setThirdPlaceData(thirdRes.data);
                        } catch {
                            if (!isStale()) setThirdPlaceData(null);
                        }
                    }
                }
            }
        } catch (error) {
            if (isStale()) return;
            setStageError(error);
            setStageData(null);
            setNextStageData(null);
            setThirdPlaceData(null);
        } finally {
            if (!isStale()) setStageLoading(false);
        }
    }, [leagueId, stages]);

    return {
        currentNode,
        stages,
        leagueId,
        stageData,
        nextStageData,
        thirdPlaceData,
        stageLoading,
        stageError,
        fetchStage,
    };
};

export default useSportStage;
